import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PullToRefresh from 'react-simple-pull-to-refresh';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';

import { Capacitor } from '@capacitor/core';
import clsx from 'clsx';

import { LoginUserStatus, useAuth } from '../../../context/AuthContext';
import { useBackLayerStack } from '../../../context/BackLayerStackContext';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useResolvedContainerErrorState } from '../../../utils/network/use-resolved-container-error-state';
import InlineContainerError from '../../reusable/inline-container-error/InlineContainerError';
import InAppNotificationsNavButton from '../in-app-notifications/InAppNotificationsNavButton';
import Loader from '../loader/Loader';
import { useStyles } from './PageContainer.styles';
import {
  IPageContainerRouteErrorState,
  PageContainerErrorHandlerContext,
} from './page-container-error-handler';
import { PageContainerRefreshHandlerContext } from './page-refresh-handler';

type PageContainerRefreshFn = () => unknown;

interface PageContainerProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
  backPath?: string;
  backButton?: boolean;
  headerContent?: React.ReactNode;
  onRefresh?: PageContainerRefreshFn;
}

const PageContainer = (props: PageContainerProps) => {
  const {
    children,
    title,
    description,
    backPath,
    backButton,
    headerContent,
    onRefresh: onRefreshProp,
  } = props;
  const { navigate } = useNavigationStack();
  const { tryDismissTop } = useBackLayerStack();
  const theme = useTheme();
  const { loginUserStatus } = useAuth();
  const isMobileHeader = useMediaQuery(theme.breakpoints.down('md'));
  const showMobileNotifications =
    isMobileHeader && loginUserStatus === LoginUserStatus.loggedIn;

  const { classes } = useStyles(theme);
  const { t } = useTranslation();

  const routeRefreshRef = useRef<PageContainerRefreshFn | null>(null);
  const [routeErrorState, setRouteErrorState] =
    useState<IPageContainerRouteErrorState | null>(null);
  const registerRouteRefreshHandler = useCallback(
    (handler: PageContainerRefreshFn | null) => {
      routeRefreshRef.current = handler;
    },
    [],
  );
  const registerRouteErrorState = useCallback(
    (state: IPageContainerRouteErrorState | null) => {
      setRouteErrorState(state);
    },
    [],
  );

  const handlePullRefresh = useCallback(async () => {
    if (onRefreshProp) {
      await Promise.resolve(onRefreshProp());
    }
    if (routeRefreshRef.current) {
      await Promise.resolve(routeRefreshRef.current());
    }
  }, [onRefreshProp]);

  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const pullEnabled = isNativePlatform && Boolean(onRefreshProp);
  const { shouldShowErrorScreen, inlineStatus } =
    useResolvedContainerErrorState({
      errors: routeErrorState?.errors ?? [],
      isLoading: routeErrorState?.isLoading ?? false,
      hasData: routeErrorState?.hasData ?? false,
    });

  const header = useMemo(() => {
    const onBackClick = () => {
      const navigateBack = () =>
        navigate((backPath || -1) as any, { replace: !!backPath });
      if (tryDismissTop(navigateBack)) {
        return;
      }
      navigateBack();
    };

    return (
      <Box className={classes.pageContainerHeader}>
        {(title || backPath || backButton || description) && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            gap={1}>
            <Box
              className={clsx(
                classes.pageContainerTitle,
                (backPath || backButton) && classes.clickable,
              )}
              onClick={() => (backButton || backPath) && onBackClick()}>
              {(backPath || backButton) && <ArrowBackIcon />}
              <Typography variant="h2" className={classes.title}>
                {title}
              </Typography>
            </Box>
            {description && (
              <Typography
                variant="label-text-md"
                className={classes.description}>
                {description}
              </Typography>
            )}
            <Box />
          </Box>
        )}
        <Box className={classes.pageHeaderActions}>
          {headerContent}
          {showMobileNotifications ? <InAppNotificationsNavButton /> : null}
        </Box>
      </Box>
    );
  }, [
    headerContent,
    showMobileNotifications,
    title,
    backPath,
    backButton,
    description,
    navigate,
    tryDismissTop,
    classes,
  ]);

  const scrollableInner = children;

  return (
    <Box className={classes.pageContainerWrapper}>
      <Box className={classes.pageContainer}>
        <Box className={classes.pageHeaderWrapper}>{header}</Box>
        <Box className={classes.pageContainerContent}>
          <PageContainerRefreshHandlerContext.Provider
            value={registerRouteRefreshHandler}>
            <PageContainerErrorHandlerContext.Provider
              value={registerRouteErrorState}>
              {shouldShowErrorScreen && inlineStatus && (
                <InlineContainerError
                  status={inlineStatus}
                  onRetry={
                    inlineStatus === 500 ? routeErrorState?.onRetry : undefined
                  }
                />
              )}
              <Box display={shouldShowErrorScreen ? 'none' : 'block'}>
                {pullEnabled ? (
                  <PullToRefresh
                    isPullable
                    onRefresh={handlePullRefresh}
                    pullDownThreshold={250}
                    maxPullDownDistance={251}
                    pullingContent={
                      <Box
                        style={{
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: 30,
                        }}>
                        <Typography variant="text-lg">
                          {t('common.pullToRefresh')}
                        </Typography>
                      </Box>
                    }
                    refreshingContent={
                      <Box
                        style={{
                          position: 'relative',
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Loader />
                      </Box>
                    }>
                    {scrollableInner as React.ReactElement}
                  </PullToRefresh>
                ) : (
                  scrollableInner
                )}
              </Box>
            </PageContainerErrorHandlerContext.Provider>
          </PageContainerRefreshHandlerContext.Provider>
        </Box>
      </Box>
    </Box>
  );
};

export default PageContainer;
